const products = [
  {
    id: "vpn",
    name: "NURA VPN",
    tagline: "Privacy Without Compromise",
    description:
      "A high-performance virtual private network for iOS that encrypts your internet traffic and masks your digital identity. Browse freely, securely, and without being tracked.",
    highlights: [
      { icon: "ri-shield-keyhole-line", text: "Military-grade AES-256 encryption" },
      { icon: "ri-global-line", text: "Servers across multiple countries" },
      { icon: "ri-eye-off-line", text: "Strict zero-log policy" },
      { icon: "ri-flashlight-line", text: "High-speed tunneling protocol" },
      { icon: "ri-wifi-off-line", text: "Automatic kill switch protection" },
    ],
    image: "https://public.readdy.ai/ai/img_res/0e08680ea92b376c10b534a197a8320c.jpg",
    accent: "from-slate-900 to-gray-800",
    tag: "Security",
  },
  {
    id: "cleaner",
    name: "NURA Cleaner",
    tagline: "Performance, Restored",
    description:
      "An intelligent iOS cleaning tool that removes junk files, duplicate photos, and app clutter. Reclaim storage space and restore your device to peak performance effortlessly.",
    highlights: [
      { icon: "ri-delete-bin-line", text: "Smart junk file detection" },
      { icon: "ri-image-2-line", text: "Duplicate photo removal" },
      { icon: "ri-dashboard-line", text: "Real-time storage analytics" },
      { icon: "ri-battery-charge-line", text: "Battery health optimization" },
      { icon: "ri-archive-line", text: "App cache management" },
    ],
    image: "https://readdy.ai/api/search-image?query=elegant%20iPhone%20mockup%20showing%20a%20clean%20minimalist%20mobile%20app%20interface%20with%20storage%20optimization%20charts%20soft%20pastel%20white%20light%20background%20premium%20iOS%20app%20design%20with%20abstract%20geometric%20light%20shapes%20floating%20device&width=600&height=750&seq=nura-cleaner-01&orientation=portrait",
    accent: "from-cyan-600 to-teal-700",
    tag: "Performance",
  },
];

export default function ProductsSection() {
  return (
    <section id="products" className="py-32" style={{ background: "linear-gradient(160deg, #f8fafc 0%, #f0fdfc 100%)" }}>
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-cyan-400" />
            <span className="text-cyan-500 text-xs uppercase tracking-[0.25em] font-medium">Our Products</span>
            <span className="h-px w-8 bg-cyan-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Built for the <span className="font-semibold">iOS ecosystem</span>
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl text-base leading-relaxed">
            Two powerful applications crafted to protect and optimize your iPhone and iPad experience.
          </p>
        </div>

        {/* Products */}
        <div className="flex flex-col gap-16">
          {products.map((product, idx) => (
            <div
              key={product.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                idx % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Image side */}
              <div className={`${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className={`relative rounded-2xl bg-gradient-to-br ${product.accent} p-10 flex items-center justify-center overflow-hidden`} style={{ minHeight: 440 }}>
                  {/* Decorative rings */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-80 h-80 rounded-full border border-white/10" />
                    <div className="absolute w-56 h-56 rounded-full border border-white/10" />
                  </div>
                  <div className="relative z-10 w-56 h-auto">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain object-top"
                    />
                  </div>
                  {/* Tag */}
                  <div className="absolute top-5 left-5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs font-medium tracking-wide">
                    {product.tag}
                  </div>
                </div>
              </div>

              {/* Content side */}
              <div className={`${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <h3
                  className="text-3xl md:text-4xl font-semibold text-gray-900 mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {product.name}
                </h3>
                <p className="text-cyan-500 font-medium text-sm uppercase tracking-widest mb-5">
                  {product.tagline}
                </p>
                <p className="text-gray-500 text-base leading-relaxed mb-8">
                  {product.description}
                </p>

                {/* Highlights */}
                <ul className="flex flex-col gap-3">
                  {product.highlights.map((h) => (
                    <li key={h.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyan-50 text-cyan-500 flex-shrink-0">
                        <i className={`${h.icon} text-sm`} />
                      </div>
                      <span className="text-gray-700 text-sm">{h.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
